'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, Paper, Stack, TextField, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  buildFundInvestmentSimulation,
  type SimulationDividendEvent,
  type SimulationPricePoint,
} from '@/libs/simulation-return';
import { getCorporateEventColor, getCorporateEventDisplayLabel } from '@/libs/corporate-event-chart';
import { useAppColors } from '@/styles/colors';

type TickerSimulationTabClientProps = {
  tickerLabel: string;
  priceSeries: SimulationPricePoint[];
  ifixSeries: SimulationPricePoint[];
  ipcaSeries: Array<{ date: string; value: number }>;
  dividendSeries: SimulationDividendEvent[];
  corporateEventSeries: Array<{
    date: string;
    eventType: string;
    label: string | null;
    splitMultiplier: number | null;
    subscriptionPercentage: number | null;
  }>;
};

type PresetRange = {
  id: '3M' | '6M' | '1A' | '2A' | '5A' | '10A' | 'MAX';
  shortLabel: string;
  periodLabel: string;
  days: number | null;
};

type ScenarioResult = {
  id: PresetRange['id'];
  shortLabel: string;
  periodLabel: string;
  requestedStartDate: string;
  actualStartDate: string;
  actualEndDate: string;
  initialAmount: number;
  finalPriceOnly: number;
  finalReinvested: number;
  gainReinvested: number;
  returnReinvestedPct: number;
  returnPriceOnlyPct: number;
};

const PRESET_RANGES: PresetRange[] = [
  { id: '3M', shortLabel: '3M', periodLabel: '3 meses', days: 91 },
  { id: '6M', shortLabel: '6M', periodLabel: '6 meses', days: 182 },
  { id: '1A', shortLabel: '1A', periodLabel: '1 ano', days: 365 },
  { id: '2A', shortLabel: '2A', periodLabel: '2 anos', days: 365 * 2 },
  { id: '5A', shortLabel: '5A', periodLabel: '5 anos', days: 365 * 5 },
  { id: '10A', shortLabel: '10A', periodLabel: '10 anos', days: 365 * 10 },
  { id: 'MAX', shortLabel: 'MÁX', periodLabel: 'máximo histórico', days: null },
];

const DAY_MS = 24 * 60 * 60 * 1000;
const BRL_FORMATTER = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
});
const PERCENT_FORMATTER = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const QUANTITY_FORMATTER = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const QUANTITY_TOOLTIP_FORMATTER = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 4,
  maximumFractionDigits: 4,
});
const DATE_FORMATTER = new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short' });
const getTodayKeyInBrt = () =>
  new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Sao_Paulo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());

const toDay = (value: string) => value.slice(0, 10);
const toTs = (value: string) => new Date(`${toDay(value)}T00:00:00Z`).getTime();
const mapValueToY = (value: number, minValue: number, maxValue: number, chartTop: number, chartHeight: number) => {
  if (maxValue === minValue) return chartTop + chartHeight / 2;
  const ratio = (value - minValue) / (maxValue - minValue);
  return chartTop + chartHeight - ratio * chartHeight;
};

const parseMoneyInput = (value: string): number | null => {
  const normalized = value.replace(/\./g, '').replace(',', '.').trim();
  const parsed = Number(normalized);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return parsed;
};

const formatSignedPercent = (value: number) => `${value >= 0 ? '+' : ''}${PERCENT_FORMATTER.format(value)}%`;

const isoToBrDate = (value: string) => {
  const [year, month, day] = value.split('-');
  if (!year || !month || !day) return value;
  return `${day}/${month}/${year}`;
};

const brToIsoDate = (value: string): string | null => {
  const cleaned = value.replace(/\D/g, '').slice(0, 8);
  if (cleaned.length !== 8) return null;
  const day = cleaned.slice(0, 2);
  const month = cleaned.slice(2, 4);
  const year = cleaned.slice(4, 8);
  const iso = `${year}-${month}-${day}`;
  const parsed = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return null;
  if (parsed.toISOString().slice(0, 10) !== iso) return null;
  return iso;
};

const normalizeBrDateInput = (value: string) => {
  const digits = value.replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
};

const HYDRATION_SAFE_INPUT_SLOT_PROPS = {
  htmlInput: {
    suppressHydrationWarning: true,
  },
};

export function TickerSimulationTabClient({
  tickerLabel,
  priceSeries,
  ifixSeries,
  ipcaSeries,
  dividendSeries,
  corporateEventSeries,
}: TickerSimulationTabClientProps) {
  const colors = useAppColors();
  const simColors = colors.modules.simulation;
  const rangeColors = colors.actions.range;
  const successStrong = colors.semantic.successStrong;
  const warningStrong = colors.semantic.warningStrong;
  const orderedPrices = useMemo(() => [...priceSeries].sort((a, b) => toTs(a.date) - toTs(b.date)), [priceSeries]);
  const orderedIfix = useMemo(() => [...ifixSeries].sort((a, b) => toTs(a.date) - toTs(b.date)), [ifixSeries]);
  const todayKey = getTodayKeyInBrt();
  const todayTs = toTs(todayKey);

  const minDate = orderedPrices[0]?.date ?? null;
  const maxDate = orderedPrices[orderedPrices.length - 1]?.date ?? null;

  const [amountInput, setAmountInput] = useState('100');
  const initialAmount = parseMoneyInput(amountInput) ?? 100;

  const presetScenarios = useMemo(() => {
    if (!minDate) return [] as ScenarioResult[];

    const rows: ScenarioResult[] = [];
    for (const preset of PRESET_RANGES) {
      const requestedStartDate =
        preset.days === null ? minDate : new Date(todayTs - preset.days * DAY_MS).toISOString().slice(0, 10);

      const simulation = buildFundInvestmentSimulation({
        prices: orderedPrices,
        dividends: dividendSeries,
        startDate: requestedStartDate,
        endDate: todayKey,
        initialAmount,
      });
      if (!simulation || simulation.points.length < 2) continue;

      const lastPoint = simulation.points[simulation.points.length - 1];
      const finalPriceOnly = lastPoint.priceOnlyValue;
      const finalReinvested = lastPoint.reinvestedValue;
      rows.push({
        id: preset.id,
        shortLabel: preset.shortLabel,
        periodLabel: preset.periodLabel,
        requestedStartDate,
        actualStartDate: simulation.actualStartDate,
        actualEndDate: simulation.actualEndDate,
        initialAmount: simulation.initialAmount,
        finalPriceOnly,
        finalReinvested,
        gainReinvested: finalReinvested - simulation.initialAmount,
        returnReinvestedPct: ((finalReinvested - simulation.initialAmount) / simulation.initialAmount) * 100,
        returnPriceOnlyPct: ((finalPriceOnly - simulation.initialAmount) / simulation.initialAmount) * 100,
      });
    }
    return rows;
  }, [orderedPrices, dividendSeries, minDate, todayKey, todayTs, initialAmount]);

  const defaultPreset = presetScenarios.find((item) => item.id === '1A') ?? presetScenarios[0] ?? null;
  const [selectedPresetId, setSelectedPresetId] = useState<PresetRange['id'] | 'CUSTOM'>(defaultPreset?.id ?? 'CUSTOM');
  const [startDate, setStartDate] = useState(defaultPreset?.requestedStartDate ?? minDate ?? '');
  const [endDate, setEndDate] = useState(todayKey);
  const [startDateInput, setStartDateInput] = useState(
    defaultPreset?.requestedStartDate ? isoToBrDate(defaultPreset.requestedStartDate) : '',
  );
  const [endDateInput, setEndDateInput] = useState(isoToBrDate(todayKey));
  const [hoveredPointIndex, setHoveredPointIndex] = useState<number | null>(null);
  const [visibleSeries, setVisibleSeries] = useState({
    priceOnly: true,
    reinvested: true,
    shares: true,
    adjustedPrice: false,
    ifix: false,
    ipca: true,
  });
  const chartContainerRef = useRef<HTMLDivElement | null>(null);
  const [chartViewportWidth, setChartViewportWidth] = useState(1000);

  useEffect(() => {
    setEndDate((current) => (current ? current : todayKey));
  }, [todayKey]);

  useEffect(() => {
    if (!defaultPreset) return;
    setStartDate((current) => (current ? current : defaultPreset.requestedStartDate));
  }, [defaultPreset]);

  useEffect(() => {
    if (!startDate) return;
    setStartDateInput(isoToBrDate(startDate));
  }, [startDate]);

  useEffect(() => {
    if (!endDate) return;
    setEndDateInput(isoToBrDate(endDate));
  }, [endDate]);

  useEffect(() => {
    const node = chartContainerRef.current;
    if (!node) return;
    const syncWidth = () => {
      const next = Math.max(680, Math.floor(node.clientWidth));
      setChartViewportWidth(next);
    };
    syncWidth();
    const observer = new ResizeObserver(syncWidth);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const customSimulation = useMemo(() => {
    if (!startDate || !endDate) return null;
    return buildFundInvestmentSimulation({
      prices: orderedPrices,
      dividends: dividendSeries,
      startDate,
      endDate,
      initialAmount,
    });
  }, [orderedPrices, dividendSeries, startDate, endDate, initialAmount]);

  if (!minDate || !maxDate) {
    return (
      <Paper variant='outlined' sx={{ mt: 2, p: 2, borderStyle: 'dashed', color: 'text.secondary' }}>
        Não foi possível gerar a simulação para {tickerLabel}.
      </Paper>
    );
  }

  const points = customSimulation?.points ?? [];
  const firstPoint = points[0] ?? null;
  const lastPoint = points[points.length - 1] ?? null;

  const selectedPreset =
    selectedPresetId === 'CUSTOM' ? null : (presetScenarios.find((item) => item.id === selectedPresetId) ?? null);

  const narrativeStartDate = startDate;
  const narrativeEndDate = endDate;
  const narrativeFinalPriceOnly = lastPoint?.priceOnlyValue ?? null;
  const narrativeFinalReinvested = lastPoint?.reinvestedValue ?? null;
  const narrativeReturnWithDividends =
    narrativeFinalReinvested !== null ? ((narrativeFinalReinvested - initialAmount) / initialAmount) * 100 : null;

  const width = chartViewportWidth;
  const height = 360;
  const chartLeft = 60;
  const chartRight = width - 140;
  const chartTop = 20;
  const chartBottom = height - 44;
  const chartWidth = chartRight - chartLeft;
  const chartHeight = chartBottom - chartTop;

  const ifixValuesByDate = useMemo(() => {
    if (points.length === 0 || orderedIfix.length === 0) return new Map<string, number>();

    const map = new Map<string, number>();
    let idx = 0;
    let lastClose: number | null = null;
    for (const point of points) {
      const pointDate = point.date;
      while (idx < orderedIfix.length && orderedIfix[idx].date <= pointDate) {
        lastClose = orderedIfix[idx].close;
        idx += 1;
      }
      if (lastClose !== null) {
        map.set(pointDate, lastClose);
      }
    }
    return map;
  }, [orderedIfix, points]);

  const ifixNormalizedByDate = useMemo(() => {
    if (points.length === 0) return new Map<string, number>();
    let baseClose: number | null = null;
    for (const point of points) {
      const close = ifixValuesByDate.get(point.date);
      if (close !== undefined && close > 0) {
        baseClose = close;
        break;
      }
    }
    if (!baseClose || baseClose <= 0) return new Map<string, number>();

    const map = new Map<string, number>();
    for (const point of points) {
      const close = ifixValuesByDate.get(point.date);
      if (close === undefined || close <= 0) continue;
      map.set(point.date, (initialAmount * close) / baseClose);
    }
    return map;
  }, [ifixValuesByDate, initialAmount, points]);

  const ipcaAdjustedByDate = useMemo(() => {
    if (points.length === 0 || ipcaSeries.length === 0) return new Map<string, number>();
    const ordered = [...ipcaSeries].sort((a, b) => a.date.localeCompare(b.date));
    const startMonth = points[0].date.slice(0, 7);
    let index = 0;
    let latestRate: number | null = null;
    let previousMonth: string | null = null;
    let factor = 1;
    const values = new Map<string, number>();
    for (const point of points) {
      const currentMonth = point.date.slice(0, 7);
      while (index < ordered.length && ordered[index].date.slice(0, 7) <= currentMonth) {
        latestRate = ordered[index].value;
        index += 1;
      }
      if (previousMonth !== null && previousMonth !== currentMonth && currentMonth > startMonth) {
        factor *= 1 + (latestRate ?? 0) / 100;
      }
      values.set(point.date, initialAmount * factor);
      previousMonth = currentMonth;
    }
    return values;
  }, [initialAmount, ipcaSeries, points]);

  let minValue = Infinity;
  let maxValue = -Infinity;
  for (const point of points) {
    if (visibleSeries.priceOnly) {
      minValue = Math.min(minValue, point.priceOnlyValue);
      maxValue = Math.max(maxValue, point.priceOnlyValue);
    }
    if (visibleSeries.reinvested) {
      minValue = Math.min(minValue, point.reinvestedValue);
      maxValue = Math.max(maxValue, point.reinvestedValue);
    }
    if (visibleSeries.ifix) {
      const ifixValue = ifixNormalizedByDate.get(point.date);
      if (ifixValue !== undefined) {
        minValue = Math.min(minValue, ifixValue);
        maxValue = Math.max(maxValue, ifixValue);
      }
    }
    if (visibleSeries.ipca) {
      const ipcaValue = ipcaAdjustedByDate.get(point.date);
      if (ipcaValue !== undefined) {
        minValue = Math.min(minValue, ipcaValue);
        maxValue = Math.max(maxValue, ipcaValue);
      }
    }
  }
  if (!Number.isFinite(minValue) || !Number.isFinite(maxValue)) {
    minValue = 0;
    maxValue = 1;
  }
  const headroom = Math.max(maxValue - minValue, Math.abs(maxValue), 1) * 0.08;
  const chartMin = minValue - headroom * 0.2;
  const chartMax = maxValue + headroom;

  const startTs = startDate ? toTs(startDate) : firstPoint ? toTs(firstPoint.date) : 0;
  const endTs = endDate ? toTs(endDate) : lastPoint ? toTs(lastPoint.date) : 1;
  const mapTsToX = (ts: number) => {
    if (endTs === startTs) return chartLeft + chartWidth / 2;
    return chartLeft + ((ts - startTs) / (endTs - startTs)) * chartWidth;
  };

  const priceLinePoints = points
    .map((point) => {
      const x = mapTsToX(toTs(point.date));
      const y = mapValueToY(point.priceOnlyValue, chartMin, chartMax, chartTop, chartHeight);
      return `${x},${y}`;
    })
    .join(' ');

  let sharesMin = Infinity;
  let sharesMax = -Infinity;
  let adjustedPriceMin = Infinity;
  let adjustedPriceMax = -Infinity;
  for (const point of points) {
    sharesMin = Math.min(sharesMin, point.sharesWithReinvestment);
    sharesMax = Math.max(sharesMax, point.sharesWithReinvestment);
    adjustedPriceMin = Math.min(adjustedPriceMin, point.closePrice);
    adjustedPriceMax = Math.max(adjustedPriceMax, point.closePrice);
  }
  if (!Number.isFinite(sharesMin) || !Number.isFinite(sharesMax)) {
    sharesMin = 0;
    sharesMax = 1;
  }
  if (!Number.isFinite(adjustedPriceMin) || !Number.isFinite(adjustedPriceMax)) {
    adjustedPriceMin = 0;
    adjustedPriceMax = 1;
  }
  const sharesHeadroom = Math.max(sharesMax - sharesMin, Math.abs(sharesMax), 1) * 0.08;
  const adjustedPriceHeadroom = Math.max(adjustedPriceMax - adjustedPriceMin, Math.abs(adjustedPriceMax), 1) * 0.08;
  const sharesChartMin = Math.max(0, sharesMin - sharesHeadroom * 0.2);
  const sharesChartMax = sharesMax + sharesHeadroom;
  const adjustedPriceChartMin = Math.max(0, adjustedPriceMin - adjustedPriceHeadroom * 0.2);
  const adjustedPriceChartMax = adjustedPriceMax + adjustedPriceHeadroom;

  const reinvestLinePoints = points
    .flatMap((point) => {
      const x = mapTsToX(toTs(point.date));
      const yAfter = mapValueToY(point.reinvestedValue, chartMin, chartMax, chartTop, chartHeight);
      if (point.creditedDividends <= 0) {
        return [`${x},${yAfter}`];
      }
      const valueBefore = Math.max(0, point.reinvestedValue - point.creditedDividends);
      const yBefore = mapValueToY(valueBefore, chartMin, chartMax, chartTop, chartHeight);
      return [`${x},${yBefore}`, `${x},${yAfter}`];
    })
    .join(' ');

  const sharesLinePoints = points
    .map((point) => {
      const x = mapTsToX(toTs(point.date));
      const y = mapValueToY(point.sharesWithReinvestment, sharesChartMin, sharesChartMax, chartTop, chartHeight);
      return `${x},${y}`;
    })
    .join(' ');

  const adjustedPriceLinePoints = points
    .map((point) => {
      const x = mapTsToX(toTs(point.date));
      const y = mapValueToY(point.closePrice, adjustedPriceChartMin, adjustedPriceChartMax, chartTop, chartHeight);
      return `${x},${y}`;
    })
    .join(' ');

  const ifixLinePoints = points
    .flatMap((point) => {
      const ifixValue = ifixNormalizedByDate.get(point.date);
      if (ifixValue === undefined) return [];
      const x = mapTsToX(toTs(point.date));
      const y = mapValueToY(ifixValue, chartMin, chartMax, chartTop, chartHeight);
      return [`${x},${y}`];
    })
    .join(' ');
  const ipcaLinePoints = points
    .flatMap((point) => {
      const ipcaValue = ipcaAdjustedByDate.get(point.date);
      if (ipcaValue === undefined) return [];
      const x = mapTsToX(toTs(point.date));
      const y = mapValueToY(ipcaValue, chartMin, chartMax, chartTop, chartHeight);
      return [`${x},${y}`];
    })
    .join(' ');

  const dividendMarkers = useMemo(() => {
    if (!startDate || !endDate) return [] as Array<{ date: string; amount: number }>;
    const grouped = new Map<string, number>();
    for (const dividend of dividendSeries) {
      const day = toDay(dividend.date);
      if (day < startDate || day > endDate) {
        continue;
      }
      grouped.set(day, (grouped.get(day) ?? 0) + dividend.amountPerShare);
    }
    return Array.from(grouped.entries())
      .map(([date, amount]) => ({ date, amount }))
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [dividendSeries, endDate, startDate]);

  const reinvestmentCredits = useMemo(() => {
    if (!customSimulation) return [] as Array<{ date: string; credited: number }>;
    return customSimulation.points
      .filter((point) => point.creditedDividends > 0)
      .map((point) => ({
        date: point.date,
        credited: point.creditedDividends,
      }));
  }, [customSimulation]);
  const corporateMarkers = useMemo(() => {
    if (!startDate || !endDate)
      return [] as Array<{
        date: string;
        eventType: string;
        label: string | null;
        splitMultiplier: number | null;
        subscriptionPercentage: number | null;
      }>;
    return corporateEventSeries
      .filter((item) => item.date >= startDate && item.date <= endDate)
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [corporateEventSeries, endDate, startDate]);

  useEffect(() => {
    setHoveredPointIndex((current) => {
      if (current === null) return null;
      if (current < 0 || current >= points.length) return null;
      return current;
    });
  }, [points.length]);

  const plottedPoints = points.map((point) => ({
    ...point,
    ifixValue: ifixNormalizedByDate.get(point.date) ?? null,
    ipcaValue: ipcaAdjustedByDate.get(point.date) ?? null,
    x: mapTsToX(toTs(point.date)),
    yPrice: mapValueToY(point.priceOnlyValue, chartMin, chartMax, chartTop, chartHeight),
    yReinvested: mapValueToY(point.reinvestedValue, chartMin, chartMax, chartTop, chartHeight),
    yShares: mapValueToY(point.sharesWithReinvestment, sharesChartMin, sharesChartMax, chartTop, chartHeight),
    yAdjustedPrice: mapValueToY(point.closePrice, adjustedPriceChartMin, adjustedPriceChartMax, chartTop, chartHeight),
    yIfix: (() => {
      const ifixValue = ifixNormalizedByDate.get(point.date);
      if (ifixValue === undefined) return null;
      return mapValueToY(ifixValue, chartMin, chartMax, chartTop, chartHeight);
    })(),
    yIpca: (() => {
      const ipcaValue = ipcaAdjustedByDate.get(point.date);
      if (ipcaValue === undefined) return null;
      return mapValueToY(ipcaValue, chartMin, chartMax, chartTop, chartHeight);
    })(),
  }));

  const toggleSeriesVisibility = (key: keyof typeof visibleSeries) => {
    setVisibleSeries((current) => ({ ...current, [key]: !current[key] }));
  };

  const applyPreset = (presetId: PresetRange['id']) => {
    const scenario = presetScenarios.find((item) => item.id === presetId);
    if (!scenario) return;
    setSelectedPresetId(presetId);
    setStartDate(scenario.requestedStartDate);
    setEndDate(todayKey);
  };

  const hoveredPoint =
    hoveredPointIndex !== null && hoveredPointIndex >= 0 && hoveredPointIndex < plottedPoints.length
      ? plottedPoints[hoveredPointIndex]
      : null;
  const latestDividendUntilHover = useMemo(() => {
    if (!hoveredPoint) return null as { date: string; amount: number } | null;
    let latest: { date: string; amount: number } | null = null;
    for (const marker of dividendMarkers) {
      if (marker.date <= hoveredPoint.date) {
        latest = marker;
        continue;
      }
      break;
    }
    return latest;
  }, [hoveredPoint, dividendMarkers]);
  const latestTotalCreditUntilHover = useMemo(() => {
    if (!hoveredPoint) return null as { date: string; credited: number } | null;
    let latest: { date: string; credited: number } | null = null;
    for (const credit of reinvestmentCredits) {
      if (credit.date <= hoveredPoint.date) {
        latest = credit;
        continue;
      }
      break;
    }
    return latest;
  }, [hoveredPoint, reinvestmentCredits]);

  return (
    <Stack spacing={2} sx={{ mt: 2 }}>
      <Paper variant='outlined' sx={{ p: { xs: 1.5, md: 2 } }}>
        <Stack spacing={1.25}>
          <Typography variant='h6' fontWeight={700}>
            Simulação de investimento em {tickerLabel}
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            Visão direta de quanto o investimento valeria hoje para períodos como 6 meses, 1 ano, 2 anos e também para
            datas personalizadas.
          </Typography>
          <Box
            sx={{
              mt: 0.25,
              px: 1,
              py: 0.75,
              borderRadius: 1,
              backgroundColor: 'hsl(var(--muted) / 0.35)',
            }}
          >
            <Typography variant='caption' color='text.secondary'>
              Observação: em alguns ativos, os dados históricos podem estar mais incompletos em períodos anteriores a
              março/2025, o que pode afetar a simulação com reinvestimento.
            </Typography>
          </Box>
        </Stack>

        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1.25}
          sx={{ mt: 2 }}
          alignItems={{ xs: 'stretch', md: 'center' }}
        >
          <TextField
            id='simulation-initial-amount'
            size='small'
            label='Valor inicial (R$)'
            value={amountInput}
            onChange={(event) => setAmountInput(event.target.value)}
            inputProps={{ inputMode: 'decimal' }}
            slotProps={HYDRATION_SAFE_INPUT_SLOT_PROPS}
            sx={{ minWidth: { xs: '100%', sm: 180 } }}
          />
          <TextField
            id='simulation-start-date'
            size='small'
            type='text'
            label='Data inicial'
            value={startDateInput}
            onChange={(event) => {
              setSelectedPresetId('CUSTOM');
              const formatted = normalizeBrDateInput(event.target.value);
              setStartDateInput(formatted);
              const parsedIso = brToIsoDate(formatted);
              if (!parsedIso) return;
              const minIso = minDate;
              const maxIso = endDate || todayKey;
              if (minIso && parsedIso < minIso) return;
              if (maxIso && parsedIso > maxIso) return;
              setStartDate(parsedIso);
            }}
            placeholder='DD/MM/AAAA'
            inputProps={{ inputMode: 'numeric', maxLength: 10 }}
            slotProps={HYDRATION_SAFE_INPUT_SLOT_PROPS}
            sx={{ minWidth: { xs: '100%', sm: 180 } }}
          />
          <TextField
            id='simulation-end-date'
            size='small'
            type='text'
            label='Data final'
            value={endDateInput}
            onChange={(event) => {
              setSelectedPresetId('CUSTOM');
              const formatted = normalizeBrDateInput(event.target.value);
              setEndDateInput(formatted);
              const parsedIso = brToIsoDate(formatted);
              if (!parsedIso) return;
              const minIso = startDate || minDate;
              const maxIso = todayKey;
              if (minIso && parsedIso < minIso) return;
              if (maxIso && parsedIso > maxIso) return;
              setEndDate(parsedIso);
            }}
            placeholder='DD/MM/AAAA'
            inputProps={{ inputMode: 'numeric', maxLength: 10 }}
            slotProps={HYDRATION_SAFE_INPUT_SLOT_PROPS}
            sx={{ minWidth: { xs: '100%', sm: 180 } }}
          />
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {presetScenarios.map((scenario) => (
              <Button
                key={scenario.id}
                size='small'
                variant={selectedPresetId === scenario.id ? 'contained' : 'outlined'}
                onClick={() => applyPreset(scenario.id)}
                sx={{
                  textTransform: 'none',
                  borderRadius: 999,
                  px: 1.5,
                  fontWeight: 700,
                  ...(selectedPresetId === scenario.id
                    ? {
                        bgcolor: rangeColors.selectedBg,
                        borderColor: rangeColors.selectedBorder,
                        color: rangeColors.selectedText,
                        '&:hover': {
                          bgcolor: rangeColors.selectedHoverBg,
                          borderColor: rangeColors.selectedHoverBorder,
                        },
                      }
                    : {
                        color: rangeColors.unselectedText,
                        borderColor: rangeColors.unselectedBorder,
                        '&:hover': {
                          color: rangeColors.unselectedHoverText,
                          borderColor: rangeColors.unselectedHoverBorder,
                          backgroundColor: rangeColors.unselectedHoverBg,
                        },
                      }),
                }}
              >
                {scenario.shortLabel}
              </Button>
            ))}
          </Box>
        </Stack>

        <Box sx={{ mt: 2.25 }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
              gap: 1,
            }}
          >
            {presetScenarios.map((scenario) => (
              <Paper
                key={scenario.id}
                variant='outlined'
                onClick={() => applyPreset(scenario.id)}
                sx={{
                  p: 1.2,
                  cursor: 'pointer',
                  borderWidth: selectedPresetId === scenario.id ? 2 : 1,
                  borderColor: selectedPresetId === scenario.id ? rangeColors.selectedBorder : 'hsl(var(--border))',
                  backgroundColor:
                    selectedPresetId === scenario.id ? alpha(rangeColors.selectedBg, 0.28) : 'hsl(var(--muted) / 0.16)',
                  boxShadow:
                    selectedPresetId === scenario.id ? `0 8px 20px ${alpha(rangeColors.selectedBg, 0.34)}` : 'none',
                  transition: 'all 140ms ease',
                }}
              >
                <Stack direction='row' justifyContent='space-between' alignItems='center'>
                  <Typography variant='body2' fontWeight={700}>
                    {scenario.periodLabel}
                  </Typography>
                  <Typography variant='caption' color='text.secondary'>
                    {DATE_FORMATTER.format(new Date(`${scenario.requestedStartDate}T00:00:00Z`))}
                  </Typography>
                </Stack>
                <Typography variant='h6' fontWeight={800} sx={{ mt: 0.5, lineHeight: 1.1, color: successStrong }}>
                  {BRL_FORMATTER.format(scenario.finalReinvested)}
                </Typography>
                <Typography
                  variant='caption'
                  sx={{
                    display: 'block',
                    color: successStrong,
                    fontWeight: 700,
                  }}
                >
                  {formatSignedPercent(scenario.returnReinvestedPct)}
                </Typography>
                <Typography variant='caption' sx={{ display: 'block', mt: 0.45, color: warningStrong }}>
                  Sem reinvest.: {BRL_FORMATTER.format(scenario.finalPriceOnly)} (
                  {formatSignedPercent(scenario.returnPriceOnlyPct)})
                </Typography>
              </Paper>
            ))}
          </Box>
        </Box>

        {!customSimulation || points.length < 2 ? (
          <Alert severity='info' sx={{ mt: 2 }}>
            Escolha um intervalo válido com pelo menos dois pontos de cotação.
          </Alert>
        ) : (
          <>
            <Paper variant='outlined' sx={{ mt: 2.25, p: 1.6, bgcolor: 'hsl(var(--muted) / 0.25)' }}>
              <Typography variant='body1' fontWeight={600} sx={{ lineHeight: 1.35 }}>
                Tendo investido {BRL_FORMATTER.format(initialAmount)} em{' '}
                {DATE_FORMATTER.format(new Date(`${narrativeStartDate}T00:00:00Z`))}
                {selectedPreset ? ` (${selectedPreset.periodLabel} atrás)` : ''}, na data final{' '}
                {DATE_FORMATTER.format(new Date(`${narrativeEndDate}T00:00:00Z`))} o valor seria:{' '}
                {BRL_FORMATTER.format(narrativeFinalReinvested ?? 0)} com dividendos reinvestidos.
              </Typography>
              <Typography variant='body2' color='text.secondary' sx={{ mt: 0.75 }}>
                Sem reinvestimento de dividendos: {BRL_FORMATTER.format(narrativeFinalPriceOnly ?? 0)} (
                {selectedPreset ? formatSignedPercent(selectedPreset.returnPriceOnlyPct) : '-'}
                ). Com reinvestimento: {BRL_FORMATTER.format(narrativeFinalReinvested ?? 0)} (
                {narrativeReturnWithDividends === null ? '-' : formatSignedPercent(narrativeReturnWithDividends)}
                ).
              </Typography>
            </Paper>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mt: 2.25 }}>
              <Paper variant='outlined' sx={{ p: 1.25, flex: 1 }}>
                <Typography variant='caption' color='text.secondary'>
                  Hoje sem reinvestimento
                </Typography>
                <Typography variant='h6' fontWeight={700} sx={{ color: warningStrong }}>
                  {BRL_FORMATTER.format(narrativeFinalPriceOnly ?? 0)}
                </Typography>
                <Typography variant='caption' color='text.secondary'>
                  {selectedPreset ? formatSignedPercent(selectedPreset.returnPriceOnlyPct) : '-'}
                </Typography>
              </Paper>
              <Paper variant='outlined' sx={{ p: 1.25, flex: 1 }}>
                <Typography variant='caption' color='text.secondary'>
                  Hoje com reinvestimento
                </Typography>
                <Typography variant='h6' fontWeight={700} sx={{ color: successStrong }}>
                  {BRL_FORMATTER.format(narrativeFinalReinvested ?? 0)}
                </Typography>
                <Typography variant='caption' color='text.secondary'>
                  {narrativeReturnWithDividends === null ? '-' : formatSignedPercent(narrativeReturnWithDividends)}
                </Typography>
              </Paper>
            </Stack>

            <Stack direction='row' spacing={1} useFlexGap flexWrap='wrap' sx={{ mt: 1.9 }}>
              <Button
                size='small'
                variant={visibleSeries.priceOnly ? 'contained' : 'outlined'}
                onClick={() => toggleSeriesVisibility('priceOnly')}
                sx={{
                  textTransform: 'none',
                  borderRadius: 999,
                  minHeight: 28,
                  px: 1.25,
                  fontWeight: 700,
                  ...(visibleSeries.priceOnly
                    ? {
                        bgcolor: simColors.price,
                        color: colors.semantic.white,
                        '&:hover': { bgcolor: simColors.price },
                      }
                    : { color: simColors.price, borderColor: simColors.price }),
                }}
              >
                Sem reinvestimento
              </Button>
              <Button
                size='small'
                variant={visibleSeries.reinvested ? 'contained' : 'outlined'}
                onClick={() => toggleSeriesVisibility('reinvested')}
                sx={{
                  textTransform: 'none',
                  borderRadius: 999,
                  minHeight: 28,
                  px: 1.25,
                  fontWeight: 700,
                  ...(visibleSeries.reinvested
                    ? {
                        bgcolor: simColors.reinvested,
                        color: colors.semantic.white,
                        '&:hover': { bgcolor: simColors.reinvested },
                      }
                    : {
                        color: simColors.reinvested,
                        borderColor: simColors.reinvested,
                      }),
                }}
              >
                Com reinvestimento
              </Button>
              <Button
                size='small'
                variant={visibleSeries.shares ? 'contained' : 'outlined'}
                onClick={() => toggleSeriesVisibility('shares')}
                sx={{
                  textTransform: 'none',
                  borderRadius: 999,
                  minHeight: 28,
                  px: 1.25,
                  fontWeight: 700,
                  ...(visibleSeries.shares
                    ? {
                        bgcolor: simColors.shares,
                        color: colors.semantic.white,
                        '&:hover': { bgcolor: simColors.shares },
                      }
                    : {
                        color: simColors.shares,
                        borderColor: simColors.shares,
                      }),
                }}
              >
                Cotas (eixo dir.)
              </Button>
              <Button
                size='small'
                variant={visibleSeries.adjustedPrice ? 'contained' : 'outlined'}
                onClick={() => toggleSeriesVisibility('adjustedPrice')}
                sx={{
                  textTransform: 'none',
                  borderRadius: 999,
                  minHeight: 28,
                  px: 1.25,
                  fontWeight: 700,
                  ...(visibleSeries.adjustedPrice
                    ? {
                        bgcolor: simColors.adjustedPrice,
                        color: colors.semantic.white,
                        '&:hover': { bgcolor: simColors.adjustedPrice },
                      }
                    : {
                        color: simColors.adjustedPrice,
                        borderColor: simColors.adjustedPrice,
                      }),
                }}
              >
                Preço por cota ajustado
              </Button>
              <Button
                size='small'
                variant={visibleSeries.ifix ? 'contained' : 'outlined'}
                onClick={() => toggleSeriesVisibility('ifix')}
                sx={{
                  textTransform: 'none',
                  borderRadius: 999,
                  minHeight: 28,
                  px: 1.25,
                  fontWeight: 700,
                  ...(visibleSeries.ifix
                    ? {
                        bgcolor: colors.charts.market.ifix,
                        color: colors.semantic.white,
                        '&:hover': { bgcolor: colors.charts.market.ifix },
                      }
                    : {
                        color: colors.charts.market.ifix,
                        borderColor: colors.charts.market.ifix,
                      }),
                }}
              >
                IFIX (base inicial)
              </Button>
              <Button
                size='small'
                variant={visibleSeries.ipca ? 'contained' : 'outlined'}
                onClick={() => toggleSeriesVisibility('ipca')}
                sx={{
                  textTransform: 'none',
                  borderRadius: 999,
                  minHeight: 28,
                  px: 1.25,
                  fontWeight: 700,
                  ...(visibleSeries.ipca
                    ? {
                        bgcolor: colors.charts.market.pib,
                        color: colors.semantic.white,
                        '&:hover': { bgcolor: colors.charts.market.pib },
                      }
                    : {
                        color: colors.charts.market.pib,
                        borderColor: colors.charts.market.pib,
                      }),
                }}
              >
                Capital corrigido por IPCA
              </Button>
              <Stack direction='row' spacing={0.75} alignItems='center' sx={{ ml: 0.5 }}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    bgcolor: simColors.dividends,
                    borderRadius: '50%',
                  }}
                />
                <Typography variant='caption' color='text.secondary'>
                  Dividendos ({dividendMarkers.length})
                </Typography>
              </Stack>
              <Stack direction='row' spacing={0.75} alignItems='center'>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    bgcolor: simColors.eventMarker,
                    borderRadius: 1,
                  }}
                />
                <Typography variant='caption' color='text.secondary'>
                  Eventos ({corporateMarkers.length})
                </Typography>
              </Stack>
            </Stack>

            <Box sx={{ mt: 1.8, overflowX: 'auto' }} ref={chartContainerRef}>
              <Box sx={{ minWidth: 680, width: '100%', position: 'relative' }}>
                {hoveredPoint ? (
                  <Paper
                    variant='outlined'
                    sx={{
                      position: 'absolute',
                      left: Math.min(Math.max(hoveredPoint.x + 12, 8), width - 280),
                      top: 8,
                      zIndex: 2,
                      p: 1,
                      minWidth: 220,
                      pointerEvents: 'none',
                    }}
                  >
                    <Typography variant='caption' fontWeight={700} color='text.primary'>
                      {DATE_FORMATTER.format(new Date(`${hoveredPoint.date}T00:00:00Z`))}
                    </Typography>
                    {visibleSeries.priceOnly ? (
                      <Typography variant='caption' sx={{ display: 'block', color: simColors.price }}>
                        Sem reinvest.: {BRL_FORMATTER.format(hoveredPoint.priceOnlyValue)}
                      </Typography>
                    ) : null}
                    {visibleSeries.reinvested ? (
                      <Typography variant='caption' sx={{ display: 'block', color: simColors.reinvested }}>
                        Com reinvest.: {BRL_FORMATTER.format(hoveredPoint.reinvestedValue)}
                      </Typography>
                    ) : null}
                    {visibleSeries.shares ? (
                      <Typography variant='caption' sx={{ display: 'block', color: simColors.shares }}>
                        Cotas na carteira: {QUANTITY_TOOLTIP_FORMATTER.format(hoveredPoint.sharesWithReinvestment)}
                      </Typography>
                    ) : null}
                    {visibleSeries.adjustedPrice ? (
                      <Typography
                        variant='caption'
                        sx={{
                          display: 'block',
                          color: simColors.adjustedPrice,
                        }}
                      >
                        Preço por cota ajustado: {BRL_FORMATTER.format(hoveredPoint.closePrice)}
                      </Typography>
                    ) : null}
                    {visibleSeries.ifix && hoveredPoint.ifixValue !== null ? (
                      <Typography
                        variant='caption'
                        sx={{
                          display: 'block',
                          color: colors.charts.market.ifix,
                        }}
                      >
                        IFIX (base inicial): {BRL_FORMATTER.format(hoveredPoint.ifixValue)}
                      </Typography>
                    ) : null}
                    {visibleSeries.ipca && hoveredPoint.ipcaValue !== null ? (
                      <Typography
                        variant='caption'
                        sx={{
                          display: 'block',
                          color: colors.charts.market.pib,
                        }}
                      >
                        Capital corrigido por IPCA: {BRL_FORMATTER.format(hoveredPoint.ipcaValue)}
                      </Typography>
                    ) : null}
                    <Typography variant='caption' sx={{ display: 'block', color: simColors.dividends }}>
                      Último dividendo total recebido até a data:{' '}
                      {latestTotalCreditUntilHover
                        ? `${BRL_FORMATTER.format(latestTotalCreditUntilHover.credited)} em ${DATE_FORMATTER.format(new Date(`${latestTotalCreditUntilHover.date}T00:00:00Z`))}`
                        : 'não há no período selecionado'}
                    </Typography>
                    <Typography variant='caption' sx={{ display: 'block', color: 'text.secondary' }}>
                      Último dividendo por cota ajustado até a data:{' '}
                      {latestDividendUntilHover
                        ? `${BRL_FORMATTER.format(latestDividendUntilHover.amount)} em ${DATE_FORMATTER.format(new Date(`${latestDividendUntilHover.date}T00:00:00Z`))}`
                        : 'não há no período selecionado'}
                    </Typography>
                  </Paper>
                ) : null}
                <svg width={width} height={height} role='img' aria-label='Simulação de investimento'>
                  <defs>
                    <linearGradient id='priceArea' x1='0' x2='0' y1='0' y2='1'>
                      <stop offset='0%' stopColor={`${simColors.price}38`} />
                      <stop offset='100%' stopColor={`${simColors.price}08`} />
                    </linearGradient>
                  </defs>

                  {Array.from({ length: 5 }).map((_, index) => {
                    const ratio = index / 4;
                    const y = chartTop + ratio * chartHeight;
                    const value = chartMax - ratio * (chartMax - chartMin);
                    return (
                      <g key={`y-${index}`}>
                        <line
                          x1={chartLeft}
                          y1={y}
                          x2={chartRight}
                          y2={y}
                          stroke='hsl(var(--muted-foreground) / 0.3)'
                          strokeDasharray='4 4'
                        />
                        <text x={8} y={y + 4} fill='hsl(var(--muted-foreground) / 0.9)' fontSize='12'>
                          {BRL_FORMATTER.format(value)}
                        </text>
                      </g>
                    );
                  })}

                  {visibleSeries.shares
                    ? Array.from({ length: 5 }).map((_, index) => {
                        const ratio = index / 4;
                        const y = chartTop + ratio * chartHeight;
                        const value = sharesChartMax - ratio * (sharesChartMax - sharesChartMin);
                        return (
                          <text
                            key={`y-right-${index}`}
                            x={chartRight + 8}
                            y={y + 4}
                            fill={simColors.shares}
                            fontSize='11'
                            textAnchor='start'
                          >
                            {QUANTITY_FORMATTER.format(value)}
                          </text>
                        );
                      })
                    : null}
                  {visibleSeries.adjustedPrice
                    ? Array.from({ length: 5 }).map((_, index) => {
                        const ratio = index / 4;
                        const y = chartTop + ratio * chartHeight;
                        const value = adjustedPriceChartMax - ratio * (adjustedPriceChartMax - adjustedPriceChartMin);
                        return (
                          <text
                            key={`y-right-price-${index}`}
                            x={chartRight + 66}
                            y={y + 4}
                            fill={simColors.adjustedPrice}
                            fontSize='11'
                            textAnchor='start'
                          >
                            {BRL_FORMATTER.format(value)}
                          </text>
                        );
                      })
                    : null}

                  <line
                    x1={chartLeft}
                    y1={chartBottom}
                    x2={chartRight}
                    y2={chartBottom}
                    stroke='hsl(var(--muted-foreground) / 0.7)'
                    strokeWidth={1}
                  />

                  {startDate ? (
                    <text x={chartLeft} y={height - 10} fill='hsl(var(--muted-foreground) / 0.9)' fontSize='12'>
                      {DATE_FORMATTER.format(new Date(`${startDate}T00:00:00Z`))}
                    </text>
                  ) : null}
                  {endDate ? (
                    <text x={chartRight - 85} y={height - 10} fill='hsl(var(--muted-foreground) / 0.9)' fontSize='12'>
                      {DATE_FORMATTER.format(new Date(`${endDate}T00:00:00Z`))}
                    </text>
                  ) : null}

                  {visibleSeries.priceOnly && priceLinePoints ? (
                    <polyline
                      points={`${chartLeft},${chartBottom} ${priceLinePoints} ${chartRight},${chartBottom}`}
                      fill='url(#priceArea)'
                      stroke='none'
                    />
                  ) : null}

                  {visibleSeries.priceOnly && priceLinePoints ? (
                    <polyline
                      points={priceLinePoints}
                      fill='none'
                      stroke={simColors.price}
                      strokeWidth={2.5}
                      strokeLinejoin='round'
                      strokeLinecap='round'
                    />
                  ) : null}

                  {visibleSeries.reinvested && reinvestLinePoints ? (
                    <polyline
                      points={reinvestLinePoints}
                      fill='none'
                      stroke={simColors.reinvested}
                      strokeWidth={2.2}
                      strokeLinejoin='round'
                      strokeLinecap='round'
                    />
                  ) : null}

                  {visibleSeries.shares && sharesLinePoints ? (
                    <polyline
                      points={sharesLinePoints}
                      fill='none'
                      stroke={simColors.shares}
                      strokeWidth={1.8}
                      strokeDasharray='5 4'
                      strokeLinejoin='round'
                      strokeLinecap='round'
                    />
                  ) : null}

                  {visibleSeries.adjustedPrice && adjustedPriceLinePoints ? (
                    <polyline
                      points={adjustedPriceLinePoints}
                      fill='none'
                      stroke={simColors.adjustedPrice}
                      strokeWidth={1.7}
                      strokeLinejoin='round'
                      strokeLinecap='round'
                    />
                  ) : null}

                  {visibleSeries.ifix && ifixLinePoints ? (
                    <polyline
                      points={ifixLinePoints}
                      fill='none'
                      stroke={colors.charts.market.ifix}
                      strokeWidth={1.8}
                      strokeDasharray='4 3'
                      strokeLinejoin='round'
                      strokeLinecap='round'
                    />
                  ) : null}

                  {visibleSeries.ipca && ipcaLinePoints ? (
                    <polyline
                      points={ipcaLinePoints}
                      fill='none'
                      stroke={colors.charts.market.pib}
                      strokeWidth={1.9}
                      strokeLinejoin='round'
                      strokeLinecap='round'
                    />
                  ) : null}

                  {hoveredPoint ? (
                    <line
                      x1={hoveredPoint.x}
                      y1={chartTop}
                      x2={hoveredPoint.x}
                      y2={chartBottom}
                      stroke='hsl(var(--foreground) / 0.35)'
                      strokeDasharray='4 4'
                      strokeWidth={1}
                    />
                  ) : null}

                  {plottedPoints.map((point) => (
                    <g key={`hover-${point.date}`}>
                      {visibleSeries.priceOnly ? (
                        <circle cx={point.x} cy={point.yPrice} r={1.8} fill={simColors.price} />
                      ) : null}
                      {visibleSeries.reinvested ? (
                        <circle cx={point.x} cy={point.yReinvested} r={1.8} fill={simColors.reinvested} />
                      ) : null}
                      {visibleSeries.shares ? (
                        <circle cx={point.x} cy={point.yShares} r={1.5} fill={simColors.shares} />
                      ) : null}
                      {visibleSeries.adjustedPrice ? (
                        <circle cx={point.x} cy={point.yAdjustedPrice} r={1.5} fill={simColors.adjustedPrice} />
                      ) : null}
                      {visibleSeries.ifix && point.yIfix !== null ? (
                        <circle cx={point.x} cy={point.yIfix} r={1.5} fill={colors.charts.market.ifix} />
                      ) : null}
                      {visibleSeries.ipca && point.yIpca !== null ? (
                        <circle cx={point.x} cy={point.yIpca} r={1.5} fill={colors.charts.market.pib} />
                      ) : null}
                    </g>
                  ))}

                  {dividendMarkers.map((marker) => {
                    const x = Math.max(chartLeft, Math.min(chartRight, mapTsToX(toTs(marker.date))));
                    return (
                      <g key={marker.date}>
                        <line
                          x1={x}
                          y1={chartBottom}
                          x2={x}
                          y2={chartBottom - 20}
                          stroke={simColors.dividends}
                          strokeWidth={2}
                        />
                        <circle
                          cx={x}
                          cy={chartBottom - 23}
                          r={4.5}
                          fill={simColors.dividends}
                          stroke={colors.semantic.white}
                          strokeWidth={1.5}
                        />
                        <title>{`Dividendo pago em ${DATE_FORMATTER.format(new Date(`${marker.date}T00:00:00Z`))}: ${BRL_FORMATTER.format(marker.amount)} por cota`}</title>
                      </g>
                    );
                  })}
                  {corporateMarkers.map((event, index) => {
                    const x = Math.max(chartLeft, Math.min(chartRight, mapTsToX(toTs(event.date))));
                    const titleParts: string[] = [];
                    titleParts.push(
                      `${event.label || event.eventType} em ${DATE_FORMATTER.format(new Date(`${event.date}T00:00:00Z`))}`,
                    );
                    if (event.splitMultiplier && event.splitMultiplier > 0) {
                      titleParts.push(`Fator: ${event.splitMultiplier.toLocaleString('pt-BR')}`);
                    }
                    if (event.subscriptionPercentage && event.subscriptionPercentage > 0) {
                      titleParts.push(`Subscrição: ${PERCENT_FORMATTER.format(event.subscriptionPercentage)}%`);
                    }
                    return (
                      <g key={`${event.date}-${event.eventType}-${index}`}>
                        <line
                          x1={x}
                          y1={chartTop}
                          x2={x}
                          y2={chartBottom}
                          stroke={`${simColors.eventMarker}66`}
                          strokeDasharray='3 3'
                          strokeWidth={1.5}
                        />
                        <rect
                          x={x - 4}
                          y={chartTop + 4}
                          width={8}
                          height={8}
                          rx={1}
                          fill={simColors.eventMarker}
                          stroke={colors.semantic.white}
                          strokeWidth={1.2}
                        />
                        <title>{titleParts.join(' | ')}</title>
                      </g>
                    );
                  })}

                  <rect
                    x={chartLeft}
                    y={chartTop}
                    width={chartWidth}
                    height={chartHeight}
                    fill='transparent'
                    onMouseLeave={() => setHoveredPointIndex(null)}
                    onMouseMove={(event) => {
                      if (plottedPoints.length === 0) return;
                      const rect = event.currentTarget.getBoundingClientRect();
                      const mouseX = ((event.clientX - rect.left) / rect.width) * width;
                      let closestIndex = 0;
                      let closestDistance = Number.POSITIVE_INFINITY;
                      for (let index = 0; index < plottedPoints.length; index += 1) {
                        const distance = Math.abs(plottedPoints[index].x - mouseX);
                        if (distance < closestDistance) {
                          closestDistance = distance;
                          closestIndex = index;
                        }
                      }
                      setHoveredPointIndex(closestIndex);
                    }}
                  />
                </svg>
              </Box>
            </Box>

            <Typography variant='caption' color='text.secondary' sx={{ mt: 1, display: 'block' }}>
              Janela selecionada: {DATE_FORMATTER.format(new Date(`${startDate}T00:00:00Z`))} até{' '}
              {DATE_FORMATTER.format(new Date(`${endDate}T00:00:00Z`))}.
            </Typography>
            {corporateMarkers.length > 0 ? (
              <Box sx={{ mt: 1 }}>
                <Typography variant='caption' color='text.secondary' sx={{ fontWeight: 700 }}>
                  Eventos corporativos no período
                </Typography>
                <Stack direction='row' spacing={0.75} useFlexGap flexWrap='wrap' sx={{ mt: 0.5 }}>
                  {corporateMarkers.slice(0, 8).map((event, index) => (
                    <Stack
                      key={`${event.date}-${event.eventType}-${index}`}
                      direction='row'
                      spacing={0.5}
                      alignItems='center'
                      sx={{
                        px: 0.75,
                        py: 0.35,
                        borderRadius: 1,
                        backgroundColor: 'hsl(var(--muted) / 0.35)',
                      }}
                    >
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          bgcolor: getCorporateEventColor(event),
                        }}
                      />
                      <Typography variant='caption' sx={{ color: 'text.secondary', fontWeight: 700 }}>
                        {DATE_FORMATTER.format(new Date(`${event.date}T00:00:00Z`))} ·{' '}
                        {getCorporateEventDisplayLabel(event)}
                      </Typography>
                    </Stack>
                  ))}
                  {corporateMarkers.length > 8 ? (
                    <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                      +{corporateMarkers.length - 8} eventos
                    </Typography>
                  ) : null}
                </Stack>
              </Box>
            ) : null}
          </>
        )}
      </Paper>
    </Stack>
  );
}
